"use client";

import React from "react";
import { Check, Sparkles, Flame, Shield, ArrowRight } from "lucide-react";
import { GYM_TIERS, GymTier } from "@/lib/demos/gymData";

interface MembershipTiersSectionProps {
  currency: "INR" | "USD";
  onSelectTier: (tier: GymTier) => void;
}

export default function MembershipTiersSection({
  currency,
  onSelectTier,
}: MembershipTiersSectionProps) {
  const sym = currency === "USD" ? "$" : "₹";

  return (
    <section id="membership-tiers" className="py-16 sm:py-24 bg-stone-950 text-white border-b border-stone-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14 space-y-3">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-[11px] font-bold text-amber-400 uppercase tracking-wider">
            <Sparkles className="w-3.5 h-3.5" />
            <span>Annual Memberships</span>
          </div>

          <h2 className="text-3xl sm:text-4xl font-black font-heading tracking-tight text-white">
            Engineered for Serious Lifters &amp; Executives
          </h2>

          <p className="text-xs sm:text-sm text-stone-400">
            Select the tier tailored to your athletic objectives. Every membership includes medical InBody scans and dedicated coach onboarding.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch">
          {GYM_TIERS.map((tier) => {
            const price = currency === "USD" ? tier.priceUSD : tier.priceINR;
            const isFeatured = tier.id === "tier-black";

            return (
              <div
                key={tier.id}
                className={`relative rounded-3xl p-7 flex flex-col justify-between transition-all duration-300 ${
                  isFeatured
                    ? "bg-gradient-to-b from-stone-900 to-stone-950 border-2 border-amber-500/60 shadow-2xl shadow-amber-500/10 scale-[1.02]"
                    : "bg-stone-900/60 border border-stone-800 hover:border-stone-700"
                }`}
              >
                {tier.badge && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                    <span className="px-3.5 py-1 rounded-full bg-gradient-to-r from-amber-500 to-amber-600 text-stone-950 text-[10px] font-black tracking-wider uppercase shadow-md flex items-center gap-1">
                      <Flame className="w-3 h-3 text-stone-950" />
                      {tier.badge}
                    </span>
                  </div>
                )}

                <div className="space-y-6">
                  {/* Title & Tagline */}
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">{tier.name}</h3>
                    <p className="text-xs text-stone-400 leading-relaxed min-h-[36px]">
                      {tier.tagline}
                    </p>
                  </div>

                  {/* Price */}
                  <div className="pt-2 border-t border-stone-800/80">
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-3xl sm:text-4xl font-black text-white">
                        {sym}{price.toLocaleString()}
                      </span>
                      <span className="text-xs text-stone-400 font-medium">/{tier.period}</span>
                    </div>
                    <span className="text-[10px] text-amber-400 font-semibold mt-1 block">
                      Tax invoice &amp; GST compliance receipt included
                    </span>
                  </div>

                  {/* Features List */}
                  <div className="space-y-3 pt-2">
                    <span className="text-[11px] font-bold text-stone-300 uppercase tracking-wider block">
                      Included Privileges:
                    </span>
                    <ul className="space-y-2.5">
                      {tier.features.map((feat, idx) => (
                        <li key={idx} className="flex items-start gap-2.5 text-xs text-stone-300">
                          <div className="w-4 h-4 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400 shrink-0 mt-0.5">
                            <Check className="w-2.5 h-2.5" />
                          </div>
                          <span>{feat}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Bottom CTA */}
                <div className="pt-8">
                  <button
                    onClick={() => onSelectTier(tier)}
                    className={`w-full py-3 px-4 rounded-xl font-bold text-xs transition-all flex items-center justify-center gap-2 ${
                      isFeatured
                        ? "bg-amber-500 hover:bg-amber-400 text-stone-950 shadow-lg shadow-amber-500/20"
                        : "bg-stone-800 hover:bg-stone-700 text-white border border-stone-700"
                    }`}
                  >
                    <span>Apply for {tier.name.split(" ")[0]} Tier</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
